import AppStateContext from "contexts/AppStateContext";
import { useContext, useRef, useState } from "react";
import { useHistory } from "react-router";
import SearchField from "components/SearchField";
import SearchResultsModal from "./SearchResultsModal";
import { SearchHit } from "types/SearchTypes";
import styles from "./globalSearch.module.css";

// EE-222 - the top bar search is now a single "type, hit Enter" affordance:
// GlobalSearch itself only owns the input field and the results modal's open
// state. All fetching, the editable-query refinement, and keyboard
// navigation across result rows live in SearchResultsModal.
const GlobalSearch = () => {
  const { token } = useContext(AppStateContext);
  const history = useHistory();

  // No `query` state here on purpose: SearchField owns its own input value
  // until Enter commits it, so a mirrored state would only ever lag behind
  // the box. The Enter handler reads the live DOM value instead, and the
  // modal owns the query from that point on.
  const [seedQuery, setSeedQuery] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Whether the modal was closed by choosing something, as opposed to Esc or
  // a backdrop click. Only used to decide where focus goes on the way out -
  // all navigation happens eagerly at click time, so nothing here can be
  // silently lost if the modal is reopened mid-transition.
  const navigatedRef = useRef<boolean>(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    // MUI's TextField does not forward onKeyDown to the <input>; it lands in
    // `...other` and is spread onto the wrapping FormControl div, so this
    // handler only sees the keydown because it bubbles - `currentTarget` is
    // that div and has no `.value`. Read the input through the ref instead.
    // preventDefault above also tells SearchField that Enter was handled
    // here, so it does not additionally commit the term to its own onChange.
    const value = inputRef.current?.value ?? "";
    if (!value.trim()) return;
    navigatedRef.current = false;
    setSeedQuery(value);
    setModalOpen(true);
  };

  const handleSelectHit = (hit: SearchHit) => {
    navigateToHit(hit);
    setModalOpen(false);
  };

  // "See all N" on a capped group - routes to the list page for that group
  // with the query pre-applied.
  const handleSeeAll = (path: string) => {
    history.push(path);
    navigatedRef.current = true;
    setModalOpen(false);
  };

  const handleRequestClose = () => {
    navigatedRef.current = false;
    setModalOpen(false);
  };

  // Fired by the Dialog's Transition once it has actually left the DOM.
  const handleExited = () => {
    const navigated = navigatedRef.current;
    navigatedRef.current = false;
    // Closed without choosing anything - hand focus back to the search box.
    if (!navigated) inputRef.current?.focus();
  };

  const navigateToHit = (hit: SearchHit) => {
    navigatedRef.current = true;
    if (hit.type === "order") {
      history.push(`/ordrestyring/${hit.id}`);
      return;
    }
    if (hit.type === "customer") {
      // /kunder/:userId loads that customer and opens their detail modal,
      // then replaces the URL with /kunder - see CustomerManagementUI.
      history.push(`/kunder/${hit.id}`);
      return;
    }
    // A part opens its parent order, nothing more. It used to also open the
    // 3D preview, which only works for CNC/PRINT3D/SHEET - ThreeDModal
    // resolves modelId per product type and has no branch for OTHER, so the
    // 485 OTHER order items in the parts group opened a viewer with no model
    // in it. The order view lists the part and can preview it from there.
    history.push(`/ordrestyring/${hit.orderId}`);
  };

  return (
    <div className={styles.searchContainer}>
      <SearchField
        inputRef={inputRef}
        value=""
        onChange={() => undefined}
        placeholder="Search..."
        onKeyDown={handleKeyDown}
        // The results modal carries the full rules; a second Help link in
        // the top bar would only compete with it.
        help="none"
      />
      <SearchResultsModal
        open={modalOpen}
        initialQuery={seedQuery}
        token={token}
        onSelectHit={handleSelectHit}
        onSeeAll={handleSeeAll}
        onRequestClose={handleRequestClose}
        onExited={handleExited}
      />
    </div>
  );
};

export default GlobalSearch;
